import { Outlet, createFileRoute } from "@tanstack/react-router";

import { AdminShell } from "@/components/admin/admin-shell";
import { RequireAdmin, RequireAuth } from "@/components/route-guards";
import { useAuth } from "@/lib/auth";
import { signOut } from "@/lib/platform-fetch";

/**
 * The `/admin` layout -- replaces `src/app/admin/layout.tsx`.
 *
 * That file was the single place owning admin auth and gating, and it stays that way: no
 * `/admin/*` route repeats the check. What changes is *when* it runs. The Next version resolved
 * the session and the entitlement on the server before sending any HTML, so a non-admin got a
 * redirect and never saw the shell. Here the same two facts arrive from `/v1/me`, one round
 * trip after load, so the gate renders nothing until it can answer.
 *
 * **What that costs, stated plainly.** The Next layout kept a non-admin out of the portal shell
 * *and* out of the JavaScript. This keeps them out of the shell; the JavaScript half comes back
 * from the router's per-route code splitting, since a client that never routes to `/admin/*`
 * never fetches those chunks. The backend guarantee is unchanged either way -- `require_admin`
 * enforces every action server-side and returns 404 on a miss, so this remains a UX affordance
 * rather than a security boundary, exactly as the old layout's comment said.
 */
export const Route = createFileRoute("/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  return (
    // Nested rather than combined: `RequireAuth` starts the sign-in flow for an anonymous
    // caller, while `RequireAdmin` shows a signed-in non-admin an explanation. Collapsing them
    // would send a signed-in non-admin back through Okta, which succeeds and returns them to
    // the same wall -- a loop that looks like broken auth rather than a permissions answer.
    <RequireAuth>
      <RequireAdmin>
        <AdminContent />
      </RequireAdmin>
    </RequireAuth>
  );
}

function AdminContent() {
  const { user } = useAuth();

  return (
    // `signOut` is passed as a prop, keeping `AdminShell`'s existing `() => Promise<void>`
    // contract. It was a Next Server Action before; it is a POST to `/v1/auth/logout` now, and
    // the shell neither knows nor cares which.
    <AdminShell userLabel={user?.email ?? user?.name ?? "unknown"} signOutAction={signOut}>
      <Outlet />
    </AdminShell>
  );
}
